import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import * as L from 'leaflet';
import {HttpClient} from '@angular/common/http';
import {NotificationsService} from 'angular2-notifications';
import {DesktopMapKeys, MapTileType, mapTileTypeLabels, MapType} from '../map-settings-types';
import {environment} from '../../../environments/environment';
import {catchError} from 'rxjs/operators';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {AuthorizationService} from '../../authorization/authorization.service';
import {TranslationSelectorService} from '../../shared/translation-selector.service';

const BASE_TILE_OPTIONS: L.TileLayerOptions = {
  minZoom: 4,
  maxZoom: 18,
  noWrap: true,
  bounds: [
    [-90, -180],
    [90, 180]
  ]
};

interface GoogleMapAccess {
  use_roadmap: boolean;
  use_hybrid: boolean;
}

const GOOGLE_MAP_TYPES = {
  [MapTileType.GoogleRoadmap]: 'roadmap',
  [MapTileType.GoogleHybrid]: 'satellite',
};

const GOOGLE_SESSIONS_STORAGE_KEY = 'google_map_sessions';
const CHOSEN_MAP_TYPE_STORAGE_KEY = 'chosen_map_type';

@Injectable({
  providedIn: 'root'
})
export class MapTileLayerSettingsService {
  leafletBaseLayers: BehaviorSubject<any> = new BehaviorSubject<any>({
    [MapTileType.OpenStreet]: new L.TileLayer(environment.mapTileBaseUrl + '{z}/{x}/{y}.png', {
      attribution: 'OSM',
      ...BASE_TILE_OPTIONS,
    }),
  });
  currentTileLayer: BehaviorSubject<L.TileLayer> = new BehaviorSubject<L.TileLayer>(null);
  chosenMapTileType: BehaviorSubject<MapTileType> = new BehaviorSubject<MapTileType>(MapTileType.OpenStreet);
  availableMaps: Array<MapType> = [
    {
      id: MapTileType.OpenStreet,
      name: mapTileTypeLabels[MapTileType.OpenStreet],
      iconClass: 'ic-osm',
      active: true
    },
    {
      id: MapTileType.GoMap,
      name: mapTileTypeLabels[MapTileType.GoMap],
      iconClass: 'ic-go-map',
      active: false
    },
    {
      id: MapTileType.GoogleRoadmap,
      name: mapTileTypeLabels[MapTileType.GoogleRoadmap],
      iconClass: 'ic-google',
      active: false
    },
    {
      id: MapTileType.GoogleHybrid,
      name: mapTileTypeLabels[MapTileType.GoogleHybrid],
      iconClass: 'ic-google',
      active: false
    },
    // {
    //   id: MapTileType.GoogleHybridUnofficial,
    //   name: mapTileTypeLabels[MapTileType.GoogleHybridUnofficial],
    //   iconClass: 'ic-google',
    //   active: false
    // },
    {
      id: MapTileType.Yandex,
      name: mapTileTypeLabels[MapTileType.Yandex],
      iconClass: 'ic-yandex',
      active: false
    },
  ];

  constructor(
    private http: HttpClient,
    private notif: NotificationsService,
    protected translate: TranslateService,
    protected authorizationService: AuthorizationService,
    @Inject(LOCALE_ID) protected locale,
    private translationSelector: TranslationSelectorService,
  ) {
    this.setAvailableMaps(this.authorizationService.desktopMapKeys.getValue());
    combineLatest([
      this.leafletBaseLayers,
      this.chosenMapTileType
    ]).subscribe(([layers, chosenTileName]) => {
      const availableLayerIds = Object.keys(layers);

      let currentMapType = availableLayerIds.includes(chosenTileName)
        ? chosenTileName
        : null;

      if (!currentMapType) {
        currentMapType = availableLayerIds[0];
        this.chosenMapTileType.next(currentMapType);
      }

      this.currentTileLayer.next(layers[currentMapType]);
      this.availableMaps.map((map: MapType) => map.active = map.id === currentMapType);
    });
  }

  createGoogleLayer(session: string, key: string): L.TileLayer {
    return new L.TileLayer(
      `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${key}`,
      {
        ...BASE_TILE_OPTIONS,
      }
    );
  }

  // initGoogleHybridUnofficialLayer(desktopMapKeys: DesktopMapKeys) {
  //   if (!desktopMapKeys.use_hybrid) {
  //     return;
  //   }
  //
  //   const layer = new L.TileLayer(
  //     `https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&hl=${this.translationSelector.getSelectedLanguage()}`,
  //     {
  //       attribution: 'Google Hybrid',
  //       ...BASE_TILE_OPTIONS,
  //     }
  //   );
  //
  //   this.leafletBaseLayers.next({
  //     ...this.leafletBaseLayers.getValue(),
  //     [MapTileType.GoogleHybridUnofficial]: layer
  //   });
  // }

  initGoMapLayer(desktopMapKeys: DesktopMapKeys) {
    if (!desktopMapKeys.use_gomap) {
      return;
    }

    const layer = new L.TileLayer(
      `https://tile.gomap.az/smoothtiles/maptile.do?lng=${this.translationSelector.getSelectedLanguage()}&z={z}&x={x}&y={y}`,
      {
        attribution: 'GoMap.Az',
        ...BASE_TILE_OPTIONS,
      }
    );

    this.leafletBaseLayers.next({
      ...this.leafletBaseLayers.getValue(),
      [MapTileType.GoMap]: layer
    });
  }

  setGoogleLayer(id: string, session: string, key: string) {
    const layer = this.createGoogleLayer(session, key);

    this.leafletBaseLayers.next({
      ...this.leafletBaseLayers.getValue(),
      [id]: layer
    });
  }

  restoreOrSetDefaultMap(keys: DesktopMapKeys) {
    const saved = JSON.parse(localStorage.getItem(CHOSEN_MAP_TYPE_STORAGE_KEY));

    const layers = this.leafletBaseLayers.getValue();

    if (saved && layers[saved]) {
      this.chosenMapTileType.next(saved);
      return;
    }

    if (keys.default_map) {
      this.chosenMapTileType.next(keys.default_map);
      return;
    }

    this.chosenMapTileType.next(this.availableMaps[0].id);
  }

  disableOSMIfNeeded(keys: DesktopMapKeys) {
    if (keys.enabled_osm) {
      return;
    }

    const layers = this.leafletBaseLayers.getValue();

    if (layers[MapTileType.OpenStreet]) {
      delete layers[MapTileType.OpenStreet];
      this.leafletBaseLayers.next(layers);
    }
  }

  initGoogleMaps(keys: DesktopMapKeys, access: GoogleMapAccess) {
    if (!keys.google_map_key) {
      return;
    }

    Object.entries(GOOGLE_MAP_TYPES).forEach(([id, type]) => {
      if (this.isTypeAllowed(type, access)) {
        this.checkGoogleSession(id, keys.google_map_key);
      }
    });
  }

  isTypeAllowed(type: string, access: GoogleMapAccess): boolean {
    const map = {
      roadmap: access.use_roadmap,
      satellite: access.use_hybrid,
    };

    return map[type] || false;
  }


  mapSettings(desktopMapKeys: DesktopMapKeys) {
    const googleAccess: GoogleMapAccess = {
      use_roadmap: desktopMapKeys.use_roadmap,
      use_hybrid: desktopMapKeys.use_hybrid,
    };

    this.initGoogleMaps(desktopMapKeys, googleAccess);
    // this.initGoogleHybridUnofficialLayer(desktopMapKeys);
    this.initGoMapLayer(desktopMapKeys);
    this.disableOSMIfNeeded(desktopMapKeys);
    this.restoreOrSetDefaultMap(desktopMapKeys);
  }

  setChosenMapTypeToLocalStorage(mapType: MapTileType) {
    localStorage.setItem(CHOSEN_MAP_TYPE_STORAGE_KEY, JSON.stringify(mapType));
  }

  setAvailableMaps(data: DesktopMapKeys | null) {
    if (!data) {
      this.restoreOrSetDefaultMap({default_map: null} as DesktopMapKeys);
      return;
    }

    this.mapSettings(data);
  }

  needToRefreshGoogleSession(expiry: string) {
    const now = (+new Date()) / 1000;
    return now >= parseInt(expiry, 10);
  }

  checkGoogleSession(id: string, key: string) {
    const sessions = JSON.parse(localStorage.getItem(GOOGLE_SESSIONS_STORAGE_KEY) || '{}');
    const data = sessions[id];

    if (data && data.key
      && data.session
      && data.expiry
      && data.key === key
      && !this.needToRefreshGoogleSession(data.expiry)) {
      //  Set google tile layer
      this.setGoogleLayer(id, data.session, key);
    } else {
      //  Create new google session
      this.createGoogleSession(id, key);
    }
  }

  clearGoogleSession(id: string) {
    const data = JSON.parse(localStorage.getItem(GOOGLE_SESSIONS_STORAGE_KEY) || '{}');
    delete data[id];
    localStorage.setItem(GOOGLE_SESSIONS_STORAGE_KEY, JSON.stringify(data));
  }

  createGoogleSession(id: string, key: string) {
    const mapType = GOOGLE_MAP_TYPES[id];

    this.http.post(
      `https://tile.googleapis.com/v1/createSession?key=${key}`,
      {
        mapType: mapType,
        layerTypes: ['layerRoadmap'],
        overlay: false,
        language: this.translationSelector.getSelectedLanguage(),
        region: 'RU',
      }
    ).pipe(
      catchError(err => {
        this.clearGoogleSession(id);
        this.notif.error(this.translate.instant('Google API-key not valid'));
        throw err;
      })
    )
      .subscribe(
        (result: {
          session: string,
          expiry: string,
          tileWidth: number,
          tileHeight: number,
          imageFormat: string
        }) => {
          this.setGoogleSessionToLocalStorage(id, result.session, key, result.expiry);
          this.setGoogleLayer(id, result.session, key);
        });
  }

  setGoogleSessionToLocalStorage(id: string, session: string, key: string, expiry: string) {
    const data = JSON.parse(localStorage.getItem(GOOGLE_SESSIONS_STORAGE_KEY) || '{}');

    data[id] = {
      session: session,
      key: key,
      expiry: expiry
    };

    localStorage.setItem(GOOGLE_SESSIONS_STORAGE_KEY, JSON.stringify(data));
  }
}
